"use client"

import { useEffect, useState } from 'react'
import { usePostHog } from 'posthog-js/react'
import Link from 'next/link'
import Image from 'next/image'
import { LocalShareButtons } from '@/components/blocks/local-share-buttons'
import { Button, buttonVariants } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ChevronRight, MapPin, Loader2, Search } from 'lucide-react'
import { createClient } from "@/lib/supabase/client"
import { STATE_SLUGS } from "@/lib/state-utils"

interface AdjacentCity {
  slug: string
  location_name: string
  fis_score: number
}

interface CityPageClientProps {
  cityName: string
  stateName: string
  stateCode: string
  stateSlug: string
  fisScore: number
  electricityRate: number
  climateZone: string
  adjacentCities: AdjacentCity[]
}

export function CityPageClient({
  cityName,
  stateName,
  stateCode,
  stateSlug,
  fisScore,
  electricityRate,
  climateZone,
  adjacentCities
}: CityPageClientProps) {
  const posthog = usePostHog()

  // Track page view
  useEffect(() => {
    if (posthog) {
      posthog.capture('local_city_viewed', {
        city_name: cityName,
        state_code: stateCode,
        state_name: stateName,
        fis_score: fisScore,
        electricity_rate: electricityRate,
        climate_zone: climateZone
      })
    }
  }, [posthog, cityName, stateCode, stateName, fisScore, electricityRate, climateZone])

  const locationName = `${cityName}, ${stateName}`

  return (
    <>
      {/* Share Buttons - Top */}
      <div className="mb-6">
        <LocalShareButtons
          locationName={locationName}
          variant="top"
          fisScore={fisScore}
          electricityRate={electricityRate}
        />
      </div>

      {/* Adjacent Cities Navigation */}
      {adjacentCities.length > 0 && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
          <p className="text-sm text-gray-600 mb-2 flex items-center gap-2">
            <Image
              src={`/state-flags/fridge.com-${(stateCode || '').toLowerCase()}-flag.png`}
              alt={`Flag of ${stateName}`}
              width={28}
              height={18}
              className="rounded-sm border border-gray-200"
            />
            Also in {stateName}:
          </p>
          <div className="flex flex-wrap gap-2">
            {adjacentCities.map((city) => (
              <Link
                key={city.slug}
                href={`/local/${stateSlug}/${city.slug}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-[#ff9951]/30 rounded-full text-sm text-[#ff9951] hover:border-[#ff9951] hover:bg-[#ff9951]/5 transition-all"
              >
                {city.location_name}
                {city.fis_score > 0 && (
                  <span className={`text-xs font-medium ${city.fis_score >= 90 ? 'text-[#ff9951]' : 'text-[#0469f0]'}`}>({city.fis_score})</span>
                )}
              </Link>
            ))}
            <Link
              href={`/local/${stateSlug}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#f2f7ff] border border-[#0469f0]/20 rounded-full text-sm text-[#0469f0] hover:border-[#0469f0] transition-colors"
            >
              View All {stateName}
              <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      )}
    </>
  )
}

// Bottom share section - to be placed at end of page content
export function CityPageShareBottom({
  cityName,
  stateName,
  fisScore,
  electricityRate
}: {
  cityName: string
  stateName: string
  fisScore: number
  electricityRate: number
}) {
  const locationName = `${cityName}, ${stateName}`

  return (
    <div className="mt-8 pt-6 pb-8 border-t border-gray-200">
      <LocalShareButtons
        locationName={locationName}
        variant="bottom"
        fisScore={fisScore}
        electricityRate={electricityRate}
      />
    </div>
  )
}

// ZIP preview data interface
interface ZipPreviewData {
  zip: string
  city_slug: string
  city_name: string
  state_code: string
  rate: number
  utility_name: string | null
}

// ZIP Code lookup component for city pages - with preview for better SEO
export function CityZipLookup({
  cityName,
  citySlug,
  stateCode,
  stateName,
  zipCount
}: {
  cityName: string
  citySlug: string
  stateCode: string
  stateName: string
  zipCount: number
}) {
  const posthog = usePostHog()
  const [zipCode, setZipCode] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [previewData, setPreviewData] = useState<ZipPreviewData | null>(null)

  const handleZipLookup = async () => {
    setError('')
    setPreviewData(null)
    setIsLoading(true)
    
    const cleanZip = zipCode.replace(/\D/g, '')
    if (cleanZip.length !== 5) {
      setError('Please enter a valid 5-digit zip code')
      setIsLoading(false)
      return
    }

    try {
      const supabase = createClient()
      const { data: zipData, error: dbError } = await supabase
        .from('zip_energy_data')
        .select('zip, city_slug, city_name, state_code, rate, utility_name')
        .eq('zip', cleanZip)
        .single()

      if (dbError || !zipData) {
        setError(`ZIP ${cleanZip} not found. Try a ZIP code in ${cityName} or nearby.`)
        setIsLoading(false)
        return
      }

      // Track ZIP lookup (preview shown)
      if (posthog) {
        posthog.capture('zip_lookup', {
          source_page: 'city',
          source_city: cityName,
          source_state: stateCode,
          searched_zip: cleanZip,
          found_city: zipData.city_slug,
          found_state: zipData.state_code,
          preview_shown: true
        })
      }

      // Show preview instead of navigating
      setPreviewData(zipData as ZipPreviewData)
      setIsLoading(false)
    } catch {
      setError('Could not look up ZIP code. Please try again.')
      setIsLoading(false)
    }
  }

  const handleViewDetails = () => {
    if (!previewData) return
    
    // Track click-through from preview
    if (posthog) {
      posthog.capture('zip_preview_click', {
        source_page: 'city',
        source_city: cityName,
        source_state: stateCode,
        zip: previewData.zip,
        target_city: previewData.city_slug
      })
    }
  }

  const clearPreview = () => {
    setPreviewData(null)
    setZipCode('')
  }

  return (
    <div className="mb-6 p-5 sm:p-6 bg-gray-50 border border-gray-100 rounded-xl">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-lg bg-[#ff9951]/10 flex items-center justify-center">
            <Search className="w-5 h-5 text-[#ff9951]" />
          </div>
          <div>
            <p className="font-bold text-gray-900">
              Find Your ZIP Electricity Estimate
            </p>
            <p className="text-xs text-gray-500">
              {zipCount > 0 
                ? `${zipCount} ZIP codes in ${cityName} with utility-average estimates`
                : `Enter your ZIP for utility-average estimates in ${cityName}, ${stateName}`
              }
            </p>
          </div>
        </div>
        
        {/* Search Input */}
        <div className="flex gap-2 max-w-md">
          <Input
            type="text"
            name="zip"
            autoComplete="postal-code"
            inputMode="numeric"
            aria-label="ZIP code"
            placeholder="Enter ZIP code"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleZipLookup()}
            className="flex-1"
            maxLength={5}
          />
          <Button onClick={handleZipLookup} variant="outline" size="default" disabled={isLoading}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <MapPin className="h-4 w-4 mr-1" />
            )}
            {isLoading ? 'Finding...' : 'Find'}
          </Button>
        </div>
        
        {error && (
          <p className="text-red-600 text-sm">{error}</p>
        )}
        
        {/* Preview Card */}
        {previewData && (
          <div className="mt-2 p-4 bg-white rounded-lg border border-[#0469f0]/30 shadow-sm">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-lg font-semibold text-gray-900">
                  {previewData.city_name}, {previewData.state_code} {previewData.zip}
                </p>
                <div className="mt-2 grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Electricity Rate</p>
                    <p className="text-xl font-bold text-[#0469f0]">{previewData.rate.toFixed(1)}¢/kWh</p>
                  </div>
                  {previewData.utility_name && (
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Utility Provider</p>
                      <p className="text-sm font-medium text-gray-900">{previewData.utility_name}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Link
                href={`/local/${STATE_SLUGS[previewData.state_code]}/${previewData.city_slug}/${previewData.zip}`}
                onClick={handleViewDetails}
                className={buttonVariants({ variant: "ctaPrimary", className: "w-full flex-1" })}
              >
                View Full Details
                <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
              <Button variant="outline" size="default" onClick={clearPreview}>
                Search Another
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
